import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import zhHK from "./locales/zh-HK.json";
import zhCN from "./locales/zh-CN.json";
import zhTW from "./locales/zh-TW.json";

const SUPPORTED = ["en", "zh-HK", "zh-TW", "zh-CN"];

// Strict-English fallback: any detected language that isn't literally one of
// zh-HK / zh-TW / zh-CN falls to "en". This covers ambiguous / generic Chinese
// codes (zh, zh-Hans, zh-Hant, zh-SG, zh-MO), every European language, every
// browser default (fr, de, ja, ko, ru, es…), and any garbled cache value from
// an old build. Explicit `?locale=en` also flows through here and is honored.
function normalizeToSupported(raw) {
  if (!raw || typeof raw !== "string") return "en";
  const v = raw.trim();
  if (SUPPORTED.includes(v)) return v;              // exact match — great
  // Case-insensitive fixups (e.g. "zh-hk" from a hand-typed URL)
  const found = SUPPORTED.find((s) => s.toLowerCase() === v.toLowerCase());
  if (found) return found;
  // Anything else — including any generic Chinese variant we don't ship —
  // strictly falls to English.
  return "en";
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      "zh-HK": { translation: zhHK },
      "zh-TW": { translation: zhTW },
      "zh-CN": { translation: zhCN },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED,
    // With this off, i18next won't auto-broaden "zh" → "zh-HK" or similar.
    nonExplicitSupportedLngs: false,
    // Never widen a specific tag like "zh-HK" to bare "zh".
    load: "currentOnly",
    interpolation: { escapeValue: false },
    detection: {
      // URL first (so ?locale=en / ?locale=zh-HK from hreflang or share links
      // routes users immediately and — via `caches` below — persists the
      // choice), then previously-picked from localStorage, then browser default.
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "locale",
      lookupLocalStorage: "midnight-lang",
      caches: ["localStorage"],
      // Runs on EVERY raw detected value (URL / cache / navigator) before
      // i18next's own matching pass. We coerce any non-supported string —
      // including "zh", "zh-Hans", "zh-Hant", "zh-SG", "fr", garbage from a
      // stale build's cache — into strict English. Explicit ?locale=en and
      // the three supported Chinese variants pass through untouched.
      convertDetectedLanguage: (lng) => normalizeToSupported(lng),
    },
  });

// Post-init strict-fallback enforcer. If the detector settled on anything
// outside our 4 supported locales (e.g. a stale `zh-Hans` string in
// localStorage from a previous build), snap the app back to English and
// overwrite the cache so the next reload is clean.
const settled = i18n.language;
const normalized = normalizeToSupported(settled);
if (settled !== normalized) {
  i18n.changeLanguage(normalized);
  try {
    localStorage.setItem("midnight-lang", normalized);
  } catch {
    /* private mode — ignore */
  }
}

// Also policy the language on every subsequent change (defensive; catches
// any dynamic changeLanguage() call from the app with a bad value).
i18n.on("languageChanged", (lng) => {
  const norm = normalizeToSupported(lng);
  if (lng !== norm) i18n.changeLanguage(norm);
});

export const LANG_OPTIONS = [
  { code: "en", label: "EN" },
  { code: "zh-HK", label: "繁中·港" },
  { code: "zh-TW", label: "繁中·台" },
  { code: "zh-CN", label: "简中" },
];

export default i18n;
